import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, ArrowRight, Feather } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ContactPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const TELEGRAM_BOT_TOKEN = '7792080768:AAGdZ6jv4H4ueprl4aPJpgXRSmuGaxfxcxo';
  const TELEGRAM_CHAT_ID = '1523610682';

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const message = `📬 New Contact Form Submission\n\n` +
        `👤 Name: ${formData.name}\n` +
        `📧 Email: ${formData.email}\n` +
        `📋 Subject: ${formData.subject}\n` +
        `📝 Message: ${formData.message}`;

      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML'
        })
      });

      if (!response.ok) throw new Error('Failed to send');

      alert('Thank you for your message! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      alert('There was an error. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: <Mail className="w-5 h-5" />, label: 'Email', value: 'sales@writetosite.com', href: 'mailto:sales@writetosite.com' },
    { icon: <Phone className="w-5 h-5" />, label: 'Phone', value: '(888) 522-4733', href: 'tel:+18885224733' },
    { icon: <MapPin className="w-5 h-5" />, label: 'Address', value: '30 N Gould Street, Ste R, Sheridan, WY 82801' },
    { icon: <Clock className="w-5 h-5" />, label: 'Hours', value: 'Mon-Fri: 9:00 AM - 6:00 PM EST' }
  ];

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231a1a2e' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-gold-200/20 rounded-full blur-3xl" />

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold-500" />
              <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
                Get in Touch
              </span>
              <span className="w-8 h-px bg-gold-500" />
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
              Contact
              <span className="block mt-2 text-gradient-gold">Us</span>
            </h1>

            <p className="font-body text-xl text-ink-600 leading-relaxed max-w-2xl mx-auto">
              We'd love to hear from you. Fill out the form below or reach out to us directly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">

            {/* Contact Info */}
            <div className={`lg:col-span-2 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <h2 className="font-display text-2xl font-bold text-ink-900 mb-8">Contact Information</h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-gold-100 text-gold-600 rounded-lg flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <div className="font-sans text-sm text-ink-500 mb-1">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="font-body text-ink-900 hover:text-gold-600 transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-body text-ink-900">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Note */}
              <div className="p-6 bg-ink-900 border border-ink-800">
                <div className="flex items-center gap-3 mb-4">
                  <Feather className="w-5 h-5 text-gold-400" />
                  <h3 className="font-display text-lg font-semibold text-cream-50">Ready to Start?</h3>
                </div>
                <p className="font-body text-cream-300 text-sm leading-relaxed mb-4">
                  For a faster response, include details about your book(s) and the type of website you're looking for.
                </p>
                <a href="/#contact" className="inline-flex items-center gap-2 text-gold-400 font-sans text-sm">
                  <span className="w-4 h-px bg-gold-500" />
                  Use our detailed form
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className={`lg:col-span-3 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="bg-cream-100 border border-cream-200 p-8 lg:p-12">
                <h2 className="font-display text-2xl font-bold text-ink-900 mb-8">Send us a Message</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-ink-700 font-sans text-sm mb-2">Your Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="input-luxury"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-ink-700 font-sans text-sm mb-2">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="input-luxury"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-ink-700 font-sans text-sm mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="input-luxury"
                      placeholder="How can we help?"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-ink-700 font-sans text-sm mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="input-luxury min-h-[180px] resize-none"
                      placeholder="Tell us about your project..."
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto disabled:opacity-50"
                  >
                    <span className="flex items-center gap-3">
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="w-5 h-5" />
                    </span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
